import { Inject, Injectable } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { LOCAL_STORAGE, WebStorageService } from "ngx-webstorage-service";
import { Observable } from "rxjs";
import { GlobalVariable } from "../app.component";
import { DataService, Response } from "../dataService/data.service";
import { ReviewRatingWeighted } from "./interface/reviews";
import {
  ReviewStructure,
  REVIEW_SUBFEATURES,
} from "../services/features-by-plan/interface/reviews";
import { FeaturesByPlanService } from "../services/features-by-plan/features-by-plan.service";
@Injectable({
  providedIn: "root",
})
export class ReviewsService {
  private BASE_URL = GlobalVariable;
  public REVIEW_WEIGHTED = {
    POSITIVE: "POSITIVE",
    NEGATIVE: "NEGATIVE",
    NEUTRAL: "NEUTRAL",
  };
  public REVIEW_SOURCE = {
    GOOGLE: "GOOGLE",
    FACEBOOK: "FACEBOOK",
    INTERNAL: "INTERNAL",
  };
  public REVIEW_ACTIONS = {
    NEW: "new",
    EDIT: "edit",
    REPLY: "reply",
  };
  reviewFeature: ReviewStructure = {
    review_widget: [
      "feature_reviews",
      REVIEW_SUBFEATURES.review_widget,
      "count",
    ],
  };
  constructor(
    private httpService: DataService,
    @Inject(LOCAL_STORAGE) private storage: WebStorageService,
    private toastrService: ToastrService,
    private featuresByPlan: FeaturesByPlanService
  ) {}

  /**
   *
   * @param message Toaster message
   * @returns triggers toaster method
   */
  toaster(message: string) {
    return {
      success: this.toastrService.success(message),
      error: this.toastrService.error(message),
      warning: this.toastrService.warning(message),
    };
  }

  get is_business_view() {
    return this.storage.get("is_business_view");
  }

  /**
   * To get outlet id from storage
   */
  get outlet_id() {
    return this.storage.get("outlet_id");
  }

  /**
   * To fetch reviews
   * GET method
   * @param filters List of query params
   * @returns list of reviews for the applied filters
   */
  public getReviewsAPI(filters: {
    page?: string;
    size?: string;
    sort_order?: string;
    source?: string;
    outlet_id?: string;
    start?: string;
    end?: string;
    rating?: number;
    min_rating?: string;
    max_rating?: string;
    keyword?: string;
  }): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.REVIEW_FEED_API_V2(this.outlet_id),
      page: filters?.page,
      size: filters?.size,
      sort_order: filters?.sort_order,
      source: filters?.source,
      rating: filters?.rating,
      outlet_id: filters?.outlet_id,
      start: filters?.start,
      end: filters?.end,
      min_rating: filters?.min_rating,
      max_rating: filters?.max_rating,
      keyword: filters?.keyword,
    });
  }

  /**
   * To download reviews
   * GET method
   * @param filters List of query params
   * @returns returns review feed report
   */
  public downloadReviewsAPI(filters: {
    sort_order: string;
    source: string;
    start: string;
    end: string;
    rating: number;
    min_rating: string;
    max_rating: string;
  }): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.REVIEW_FEED_REPORT(this.outlet_id),
      sort_order: filters?.sort_order,
      source: filters?.source,
      rating: filters?.rating,
      start: filters?.start,
      end: filters?.end,
      min_rating: filters?.min_rating,
      max_rating: filters?.max_rating,
    });
  }

  /**
   * To get reply templates
   * GET method
   * @returns Review reply templates
   */
  public getReplyTemplates() {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.SMS_REPLY_URL_GET_WOP_V2(this.outlet_id),
    });
  }
  /**
   * To get particular review based on review id
   * GET method
   * @param source Review source
   * @param review_id Review id
   * @returns Updated review
   */
  public getUpdatedReviewAPI(source: string, review_id: string) {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.GET_UPDATED_REVIEW(this.outlet_id),
      source: source,
      review_id: review_id,
    });
  }

  /**
   * To post reply to google
   * POST method
   * @param payload Review reply comment for google
   * @returns Review reply success response
   */
  public postGoogleReply(payload) {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.GOOGLE_REPLY_URL_V2(this.outlet_id),
      data: payload,
    });
  }

  /**
   * To post reply to facebook
   * POST method
   * @param payload Review reply comment for facebook
   * @returns Review reply success response
   */
  public postFacebookReply(payload) {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.FB_REPLY_URL_V2(this.outlet_id),
      data: payload,
    });
  }

  /**
   * To post reply to zceppa
   * POST method
   * @param payload Review reply comment for zceppa
   * @returns Review reply success response
   */
  public postInternalReplyAPI(payload) {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.INTERNAL_REPLY_URL_V2(this.outlet_id),
      data: payload,
    });
  }

  /**
   * To check review credit
   * PUT method
   * @param payload sms content length
   * @returns
   */
  public smsLimitCheckAPI(payload) {
    return this.httpService.putRequest({
      customApiUrl: this.BASE_URL.SMS_ALERT(this.outlet_id),
      data: payload,
    });
  }

  /**
   * To upload image to s3 for tickets
   * POST method
   * @param payload Image
   * @returns
   */
  public fileUploadAPI(payload) {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.SOCIAL_UPLOAD(this.outlet_id),
      data: payload,
    });
  }

  /**
   * To raise a ticket for a review
   * POST method
   * @param payload Ticket form data
   * @returns
   */
  public raiseTicketAPI(payload) {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.TICKET_CREATE_API(this.outlet_id),
      data: payload,
    });
  }

  /**
   * To get connected platforms
   * GET method
   * @returns
   */
  public getIntegratedPlatformAPI() {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.GOOGLE_RECONNECT_API(this.outlet_id),
    });
  }

  /**
   * To get review settings
   * @returns
   */
  public getReviewSettingsAPI() {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.REVIEW_SETUP_VALUE_URL_V2(this.outlet_id),
    });
  }

  /**
   * To get details of the reviewer
   * GET method
   * @param payload review id
   * @returns
   */
  public viewCustomerAPI(payload) {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.CUSTOMER_VIEW,
      review_id: payload,
    });
  }

  /**
   * To get list of team members for raise ticket modal
   * GET method
   * @returns
   */
  public getTeamMemberAPI() {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.TAG_TEAM_MEMBER(this.outlet_id),
      product: "TICKETS",
    });
  }

  async getReviewWidgetCountValue(): Promise<number> {
    return (
      await this.featuresByPlan.getFeature(
        this.reviewFeature["review_widget"]
      )
    ).getCount();
  }

  public getReviewInviteTemplate() {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.GET_REVIEW_WHATSAPP_TEMPLATE(this.outlet_id),
    });
  }

  public setReviewInviteDefaultTemplate(templateId: string) {
    return this.httpService.putRequest({
      customApiUrl: this.BASE_URL.SET_DEFAULT_REVIEW_WHATSAPP_TEMPLATE(
        this.outlet_id,
        templateId
      ),
      data: {},
    });
  }

  public getReviewInviteTemplateForSendReviewInvite() {
    return this.httpService.getRequest({
      customApiUrl:
        this.BASE_URL.GET_REVIEW_WHATSAPP_TEMPLATE_SEND_REVIEW_INVITE(
          this.outlet_id
        ),
    });
  }

  public sendReviewInviteViaWhatsApp(payload) {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.SEND_REVIEW_INVITE_VIA_WHATSAPP(
        this.outlet_id
      ),
      data: payload,
    });
  }
}
